import {
  Color,
  DoubleSide,
  Matrix4,
  Mesh,
  PerspectiveCamera,
  Plane,
  ShaderMaterial,
  UniformsUtils,
  Vector3,
  Vector4,
  WebGLRenderTarget,
} from 'three';

class Reflector extends Mesh {
  constructor(geometry, options = {}) {
    super(geometry);

    this.type = 'Reflector';
    this.camera = new PerspectiveCamera();

    const scope = this;

    const color = options.color !== undefined ? new Color(options.color) : new Color(0x7f7f7f);
    const textureWidth = options.textureWidth || 512;
    const lod = options.lod || 1.0;
    const textureHeight = options.textureHeight || 512;
    const time = options.time || 0.0;
    const clipBias = options.clipBias || 0;
    const normalSampler = options.waterNormals !== undefined ? options.waterNormals : null;
    const shader = options.shader || Reflector.ReflectorShader;
    const multisample = options.multisample !== undefined ? options.multisample : 4;

    //

    const reflectorPlane = new Plane();
    const normal = new Vector3();
    const reflectorWorldPosition = new Vector3();
    const cameraWorldPosition = new Vector3();
    const rotationMatrix = new Matrix4();
    const lookAtPosition = new Vector3(0, 0, -1);
    const clipPlane = new Vector4();

    const view = new Vector3();
    const target = new Vector3();
    const q = new Vector4();

    const textureMatrix = new Matrix4();
    const virtualCamera = this.camera;

    const renderTarget = new WebGLRenderTarget(textureWidth, textureHeight, { samples: multisample });

    const material = new ShaderMaterial({
      uniforms: UniformsUtils.clone(shader.uniforms),
      fragmentShader: shader.fragmentShader,
      vertexShader: shader.vertexShader,
      transparent: true,
      side: DoubleSide,
      opacity: options.opacity || 1.0,
    });

    material.uniforms['tDiffuse'].value = renderTarget.texture;
    material.uniforms['color'].value = color;
    material.uniforms['time'].value = time;
    material.uniforms['lod'].value = lod;
    material.uniforms['opacity'].value = options.opacity || 1.0;
    material.uniforms['normalSampler'].value = normalSampler;
    material.uniforms['textureMatrix'].value = textureMatrix;

    this.material = material;

    this.onBeforeRender = function (renderer, scene, camera) {
      reflectorWorldPosition.setFromMatrixPosition(scope.matrixWorld);
      cameraWorldPosition.setFromMatrixPosition(camera.matrixWorld);

      rotationMatrix.extractRotation(scope.matrixWorld);

      normal.set(0, 0, 1);
      normal.applyMatrix4(rotationMatrix);

      view.subVectors(reflectorWorldPosition, cameraWorldPosition);

      // Avoid rendering when reflector is facing away

      if (view.dot(normal) > 0) return;

      view.reflect(normal).negate();
      view.add(reflectorWorldPosition);

      rotationMatrix.extractRotation(camera.matrixWorld);

      lookAtPosition.set(0, 0, -1);
      lookAtPosition.applyMatrix4(rotationMatrix);
      lookAtPosition.add(cameraWorldPosition);

      target.subVectors(reflectorWorldPosition, lookAtPosition);
      target.reflect(normal).negate();
      target.add(reflectorWorldPosition);

      virtualCamera.position.copy(view);
      virtualCamera.up.set(0, 1, 0);
      virtualCamera.up.applyMatrix4(rotationMatrix);
      virtualCamera.up.reflect(normal);
      virtualCamera.lookAt(target);

      virtualCamera.far = camera.far; // Used in WebGLBackground

      virtualCamera.updateMatrixWorld();
      virtualCamera.projectionMatrix.copy(camera.projectionMatrix);

      // Update the texture matrix
      textureMatrix.set(0.5, 0.0, 0.0, 0.5, 0.0, 0.5, 0.0, 0.5, 0.0, 0.0, 0.5, 0.5, 0.0, 0.0, 0.0, 1.0);
      textureMatrix.multiply(virtualCamera.projectionMatrix);
      textureMatrix.multiply(virtualCamera.matrixWorldInverse);
      textureMatrix.multiply(scope.matrixWorld);

      // Now update projection matrix with new clip plane, implementing code from: http://www.terathon.com/code/oblique.html
      // Paper explaining this technique: http://www.terathon.com/lengyel/Lengyel-Oblique.pdf
      reflectorPlane.setFromNormalAndCoplanarPoint(normal, reflectorWorldPosition);
      reflectorPlane.applyMatrix4(virtualCamera.matrixWorldInverse);

      clipPlane.set(reflectorPlane.normal.x, reflectorPlane.normal.y, reflectorPlane.normal.z, reflectorPlane.constant);

      const projectionMatrix = virtualCamera.projectionMatrix;

      q.x = (Math.sign(clipPlane.x) + projectionMatrix.elements[8]) / projectionMatrix.elements[0];
      q.y = (Math.sign(clipPlane.y) + projectionMatrix.elements[9]) / projectionMatrix.elements[5];
      q.z = -1.0;
      q.w = (1.0 + projectionMatrix.elements[10]) / projectionMatrix.elements[14];

      // Calculate the scaled plane vector
      clipPlane.multiplyScalar(2.0 / clipPlane.dot(q));

      // Replacing the third row of the projection matrix
      projectionMatrix.elements[2] = clipPlane.x;
      projectionMatrix.elements[6] = clipPlane.y;
      projectionMatrix.elements[10] = clipPlane.z + 1.0 - clipBias;
      projectionMatrix.elements[14] = clipPlane.w;

      // Render

      renderTarget.texture.encoding = renderer.outputEncoding;

      scope.visible = false;

      const currentRenderTarget = renderer.getRenderTarget();

      const currentXrEnabled = renderer.xr.enabled;
      const currentShadowAutoUpdate = renderer.shadowMap.autoUpdate;

      renderer.xr.enabled = false; // Avoid camera modification
      renderer.shadowMap.autoUpdate = false; // Avoid re-computing shadows

      renderer.setRenderTarget(renderTarget);

      renderer.state.buffers.depth.setMask(true); // make sure the depth buffer is writable so it can be properly cleared, see #18897

      if (renderer.autoClear === false) renderer.clear();
      renderer.render(scene, virtualCamera);

      renderer.xr.enabled = currentXrEnabled;
      renderer.shadowMap.autoUpdate = currentShadowAutoUpdate;

      renderer.setRenderTarget(currentRenderTarget);

      // Restore viewport

      const viewport = camera.viewport;

      if (viewport !== undefined) {
        renderer.state.viewport(viewport);
      }

      scope.visible = true;
    };

    this.getRenderTarget = function () {
      return renderTarget;
    };

    this.dispose = function () {
      renderTarget.dispose();
      scope.material.dispose();
    };
  }
}

Reflector.prototype.isReflector = true;

Reflector.ReflectorShader = {
  uniforms: {
    color: {
      value: null,
    },

    tDiffuse: {
      value: null,
    },

    textureMatrix: {
      value: null,
    },

    opacity: {
      value: 1.0,
    },

    time: {
      value: 1.0,
    },

    lod: {
      value: 1.0,
    },

    normalSampler: {
      value: null,
    },
    iResolution: { value: new Vector3(1, 1, 1) },
  },

  vertexShader: /* glsl */ `
		uniform mat4 textureMatrix;
		varying vec4 vUv;
		// varying vec4 base;
    varying vec3 vNormal;
    uniform float lod;
    varying vec4 mirrorCoord;
    varying vec4 worldPosition;
    uniform sampler2D tDiffuse;

		#include <common>
		#include <logdepthbuf_pars_vertex>

		void main() {

			vUv = textureMatrix * vec4( position, 1.0 );
      mirrorCoord = modelMatrix * vec4( position, 1.0 );
      worldPosition = mirrorCoord.xyzw;
      vNormal = normal;
      // base = texture2DProj( tDiffuse, vUv );
      mirrorCoord = textureMatrix * mirrorCoord;
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

			#include <logdepthbuf_vertex>

		}`,

  fragmentShader: /* glsl */ `
		uniform vec3 color;
		uniform sampler2D tDiffuse;
    uniform float opacity;
    varying vec3 vNormal;
    // varying vec4 base;
    uniform sampler2D normalSampler;
		varying vec4 vUv;
    uniform float time;
    varying vec4 mirrorCoord;
    uniform vec3 iResolution;   
    uniform float lod;   
    varying vec4 worldPosition;
    float seg = 5.0;
    float i = 0.0;
    float j = 0.0;
    float f = 0.0f;
    float dv = 2.0f/512.0f;
    float tot = 0.0f;
    #define FLIP_IMAGE

		#include <logdepthbuf_pars_fragment>

		float blendOverlay( float base, float blend ) {

			return( base < 1.0 ? ( 0.3 * base * blend ) : ( 1.0 - 2.0 * ( 1.0 - base ) * ( 1.0 - blend ) ) );

		}

		vec3 blendOverlay( vec3 base, vec3 blend ) {

			return vec3( blendOverlay( base.r, blend.r ), blendOverlay( base.g, blend.g ), blendOverlay( base.b, blend.b ) );

		}

    float rand(vec2 uv) {
 
      float a = dot(uv, vec2(92., 80.));
      float b = dot(uv, vec2(41., 62.));
      
      float x = sin(a) + cos(b) * 51.;
      return fract(x);
      
    }
		void main() {

			#include <logdepthbuf_fragment>

      vec2 uv = vUv.xy / iResolution.xy;
      
      // vec4 base = texture( tDiffuse, vUv.xy, lod );

			vec4 base = texture2DProj( tDiffuse, vUv );
			vec4 i_color = vec4( blendOverlay( base.rgb, color ), opacity );

      gl_FragColor = i_color;
      // if (vNormal.y >= 0.5){
      //   gl_FragColor = vec4( blendOverlay( base.rgb, color ), opacity );    
      // } else {
      //   gl_FragColor =  vec4(0,0,0,0);
      // }
			#include <encodings_fragment>

		}`,
};

export { Reflector };
